/*
    Copyright (C) 2023 - Wheel and Brake - Southwest Airlines
*/
import { Button, Form } from "react-bootstrap";
import Select from "react-select";
import { useState, useEffect } from "react";
import WnbModal from "../commons/WnbModalAlert";
import FieldValidation from "../commons/FieldValidation";
import "./Part.css";
import { useNavigate } from 'react-router-dom';
import WnbModalSuccess from "../commons/WnbModalSuccess";
import WnbModalError from "../commons/WnbModalError";
import { getAll } from "../../../backend/read/GetAll";
import { getAllActive } from "../../../backend/read/GetAllActive";
import { create } from "../../../backend/create/Create";
import { update } from "../../../backend/update/Update";
import {ACCESS_TOKEN_KEY} from "../../../utilities/Constants";

const PartForm = (props) => {
  const [partStatus, setPartStatus] = useState(props.partStatus || "active");
  const [wbStatus, setWbStatus] = useState(props.wbStatus || "");
  const [ata, setAta] = useState(props.ata || "");
  const [mpn, setMpn] = useState(props.mpn || "");
  const [vendor, setVendor] = useState(props.vendor || "");
  const [effectivities, setEffectivities] = useState(props.effectivities || []);
  const [description, setDescription] = useState(props.description || "");
  const [showAlert, setShowAlert] = useState(false);
  const saveLabel = "Save";
  const [id] = useState(props.id);
  const headerLabel = id ? "Edit" : "Create";
  const [validVendor, setValidVendor] = useState(true);
  const [vendorValidationMessage, setVendorValidationMessage] = useState("");
  const [vendorValidationType, setVendorValidationType] = useState("");
  const [validDescription, setValidDescription] = useState(true);
  const [descriptionValidationMessage, setDescriptionValidationMessage] = useState("");
  const [descriptionValidationType, setDescriptionValidationType] = useState("");
  const [validMPNCode, setValidMPNCode] = useState(true);
  const [mpnValidationMessage, setMPNValidationMessage] = useState("");
  const [mpnValidationType, setMPNValidationType] = useState("");
  const [validATA, setValidATA] = useState(true);
  const [ataValidationMessage, setATAValidationMessage] = useState("");
  const [ataValidationType, setATAValidationType] = useState("");
  const navigate = useNavigate();
  const [inputDuplicateValue, setInputDuplicateValue] = useState(false);
  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [showNotSavedAlert, setShowNotSavedAlert] = useState(false);
  const [getPartMpnValues, setGetPartMpnValues] = useState([]);
  const [mode] = useState(props.mode || "create");
  const [activeEffOptions, setActiveEffOptions] = useState([]);
  const originalEffectivities = props.effectivities;
  const [successUpdate, setSuccessUpdate] = useState(true);

  const compare = (a, b) => {
    return a.label > b.label ? 1 : b.label > a.label ? -1 : 0;
  }

  useEffect(() => {
    const getActiveEff = () => {
      getAllActive("EFFECTIVITY").then(resp => {
        JSON.parse(resp.body.data).Items.forEach(i => {
          setActiveEffOptions(current => [...current, { value: i.EFFECTIVITY_ID, label: i.EFFECTIVITY_ID }].sort(compare))
        })
      })
    }
    getActiveEff();
  }, [])

  useEffect(() => {
    const getMpns = () => {
      getAll("PARTS").then(r => {
        JSON.parse(r.body.data).Items.forEach(i => {
          setGetPartMpnValues(current => [...current, i.PART_ID.toUpperCase()]);
        });
      })
    };
    getMpns();
  }, []);

  const onChangeATA = (event) => {
    const hasInvalidChar = event.target.value.match(/[^0-9]/gi);
    let newAta = event.target.value.replace(/[^0-9]/gmi, "");
    let hasClearedValue = ata.length >= newAta.length;
    const attemptExceed = newAta.length > 2;
    newAta = newAta.substring(0, 2);
    setAta(newAta);
    if (hasInvalidChar) {
      setAtaValidation(false, "ATA is not valid. Please create an ATA using two-digit values only.");
    } else if (attemptExceed) {
      setAtaValidation(false, "ATA is not valid. Please create an ATA value using two digits only.");
    } else if (newAta.length !== 0 && (hasClearedValue && newAta.length < 2)) {
      setAtaValidation(false, "ATA is not valid. Please create an ATA value using two digits only.");
    }
  };

  const onBlurATA = (event) => {
    const newATAValue = event.target.value;
    if (newATAValue.length === 1) {
      setAtaValidation(false, "ATA is not valid. Please create an ATA value using two digits only.");
    }
  };

  const setAtaValidation = (valid, message, type) => {
    setValidATA(valid);
    setATAValidationMessage(message || "");
    setATAValidationType(type || "error");
  };

  const closeATAValueError = () => {
    setAtaValidation(true);
  };

  const onChangeMPN = (event) => {
    if (mode === "create") {
      let newMPNVal = event.target.value;
      const attemptExceed = newMPNVal.length > 40;
      newMPNVal = newMPNVal.substring(0, 40);
      setMpn(newMPNVal.trim());
      const duplicateName = getPartMpnValues.includes(event.target.value.trim().toUpperCase());
      setInputDuplicateValue(false);
      if (attemptExceed) {
        setMPNValidation(false, "MPN is not valid. Please create a MPN using up to 40 characters.");
      }
      else if (duplicateName) {
        setMPNValidation(false, "This MPN is already defined.  Please enter a unique MPN value.");
        setInputDuplicateValue(true);
      }
    } else {
      let newMPNVal = event.target.value;
      const attemptExceed = newMPNVal.length > 40;
      newMPNVal = newMPNVal.substring(0, 40);
      setMpn(newMPNVal.trim());
      setInputDuplicateValue(false);
      if (attemptExceed) {
        setMPNValidation(false, "MPN is not valid. Please create a MPN using up to 40 characters.");
      }

    }
  };

  const closeMPNValueError = () => {
    setMPNValidation(true);
  };

  const setMPNValidation = (valid, message, type) => {
    setValidMPNCode(valid);
    setMPNValidationMessage(message || "");
    setMPNValidationType(type || "error");
  };

  const onChangeVendor = (event) => {
      const value = event.target.value;
      const attemptExceed = value.length > 40;
      let newVendorValue = value.substring(0, 40);
      setVendor(newVendorValue);
      if (attemptExceed) {
          setVendorValidation(false, "Vendor is not valid. Please create a Vendor using up to 40 characters.")
      }
  }

    const closeVendorErrorValue = () => {
        setVendorValidation(true);
    };

    const setVendorValidation = (valid, message, type) => {
        setValidVendor(valid);
        setVendorValidationMessage(message || "");
        setVendorValidationType(type || "error");
    };

  const onChangeEffectivity = (effectivities) => {
    setEffectivities(effectivities);
  }

  const onChangeDescription = (event) => {
    const value = event.target.value;
    const attemptExceed = value.length > 100;
    let newDescValue = value.substring(0, 100);
    setDescription(newDescValue);
    if (attemptExceed) {
        setDescriptionValidation(false, "Description is not valid. Please enter a Description that does not exceed 100 characters.");
    }
  };

  const closeDescriptionErrorValue = () => {
      setDescriptionValidation(true);
  };

  const setDescriptionValidation = (valid, message, type) => {
    setValidDescription(valid);
    setDescriptionValidationMessage(message || "");
    setDescriptionValidationType(type || "error");
  };

  const onSave = () => {

    let totalRequest = effectivities.length;
    if (mode === "create") {
      let successCount = 0;
      let failureCount = 0;
      effectivities.forEach(item => {
        let body = {
            "PART_ID": {
              "S": mpn.toUpperCase()
            },
            "EFFECTIVITY_ID": {
              "S": item.value
            },
            "ATA": {
              "S": ata
            },
            "DESCRIPTION": {
              "S": description
            },
            "PART_TYPE_CODE": {
              "S": wbStatus.toUpperCase()
            },
            "VENDOR": {
              "S": vendor.toUpperCase()
            },
            "ACTIVE_FLAG": {
              "BOOL": partStatus === "active"
            },
            "ACCESS_TOKEN": {
                "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
            }
        }
        create("PARTS", body).then(resp => {
          if (resp.statusCode === 200) {
            successCount = successCount + 1;
          } else {
            failureCount = failureCount + 1;
          }
          if (successCount + failureCount === totalRequest) {
            if (failureCount > 0) {
              setSaveErrorMessage(failureCount + " effectivity for Parts '" + mpn.toUpperCase() + "' was not created. Please try again.");
              setShowNotSavedAlert(true);
            } else if (successCount > 0) {
              setShowSaveAlert(true);
              setSaveMessage("Parts '" + mpn.toUpperCase() + "' saved successfully.");
            }
          }

        });
      });
    } else {
      effectivities.forEach(eff => {
        let body = {
            "PART_ID": {
              "S": mpn
            },
            "EFFECTIVITY_ID": {
              "S": eff.value.toString().trim()
            },
            "ACTIVE_FLAG": {
              "BOOL": partStatus === "active"
            },
            "ATA": {
              "S": ata
            },
            "DESCRIPTION": {
              "S": description
            },
            "PART_TYPE_CODE": {
              "S": wbStatus.toUpperCase()
            },
            "VENDOR": {
              "S": vendor
            },
            "ACCESS_TOKEN": {
                "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
            }
        }
        update("PARTS", body).then(resp => {
          if (resp.statusCode !== 200) {
            setSaveErrorMessage(eff.value + " effectivity for Parts '" + mpn.toUpperCase() + "' was not created. Please try again.");
            setShowNotSavedAlert(true);
            setSuccessUpdate(false);
          }
        });
      });
      originalEffectivities.forEach(eff => {
        if (!(effectivities.includes(eff))) {
          let body = {
              "PART_ID": {
                "S": mpn
              },
              "EFFECTIVITY_ID": {
                "S": eff.value.toString().trim()
              },
              "ACTIVE_FLAG": {
                "BOOL": partStatus === false
              },
              "ATA": {
                "S": ata
              },
              "DESCRIPTION": {
                "S": description
              },
              "PART_TYPE_CODE": {
                "S": wbStatus.toUpperCase()
              },
              "VENDOR": {
                "S": vendor
              },
              "ACCESS_TOKEN": {
                  "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
              }
          }
          update("PARTS", body).then(resp => {
            if (resp.statusCode !== 200) {
              setSaveErrorMessage(eff.value + " for Parts '" + mpn.toUpperCase() + "' was not created. Please try again.");
              setShowNotSavedAlert(true);
              setSuccessUpdate(false);
            }
          });
        }
      });
      if (successUpdate) {
        setShowSaveAlert(true);
        setSaveMessage("Parts '" + mpn.toUpperCase() + "' saved successfully.");
      }
    }
  }

  const onCancel = () => {
    setShowAlert(true);
  };

  const onClickNew = () => {
    if (mode === "edit") {
      navigate('/create-part');
    } else {
      window.location.reload();
    }
  };

  const onClickShowList = () => {
    navigate('/part-list');
  };

  const onClickOkSaveError = () => {
    setShowNotSavedAlert(false);
  };

  const onClickOkSaveSuccess = () => {
    setShowSaveAlert(false);
  };


  const onClickModalNo = () => {
    setShowAlert(false);
  }

  const onClickModalYes = () => {
    setPartStatus(props.partStatus || "active");
    setWbStatus(props.wbStatus || "");
    setAta(props.ata || "");
    setMpn(props.mpn || "");
    setVendor(props.vendor || "");
    setEffectivities(props.effectivities || "");
    setDescription(props.description || "");
    closeMPNValueError();
    closeATAValueError();
    closeVendorErrorValue();
    closeDescriptionErrorValue();
    setShowAlert(false);
  }

  return (
    <div className="form-main">
      <Form className="create-form">
        <div className="form-header">
          <div>{headerLabel} Part</div>
          <div className="header-note"><span style={{ color: 'var(--southwest-red)' }}>&#10045;</span> denotes a mandatory field</div>
        </div>
        <Form.Group className="form-status">
          <Form.Check
            name="partStatus"
            type="radio"
            className="wnb-label"
            label="ACTIVE"
            value="active"
            checked={partStatus === "active"}
            bsPrefix="printstatus-radio"
            onChange={() => { setPartStatus("active") }}
          />
          <Form.Check
            name="partStatus"
            type="radio"
            className="wnb-label"
            label="INACTIVE"
            value="inactive"
            checked={partStatus === "inactive"}
            onChange={() => { setPartStatus("inactive") }}
          />
        </Form.Group>
        <Form.Group className="form-wbstatus">
          <Form.Check
            name="wbStatus"
            type="radio"
            className="wnb-label"
            label="WHEEL"
            value="wheel"
            bsPrefix="printstatus-radio"
            checked={wbStatus === "wheel"}
            onChange={() => { setWbStatus("wheel") }}
          />
          <Form.Check
            name="wbStatus"
            type="radio"
            className="wnb-label"
            label="BRAKE"
            value="brake"
            checked={wbStatus === "brake"}
            onChange={() => { setWbStatus("brake") }}
          />
        </Form.Group>
        <Form.Group className="form-textfield">
          <Form.Label className="wnb-label" style={{ paddingLeft: '60px' }}>MPN<span style={{ color: 'var(--southwest-red)', paddingLeft: '2px' }}>&#10045;</span></Form.Label>
          <div className="field-control marginCls">
            <Form.Control
              type="mpn"
              className={(!validMPNCode && 'mpnClass invalid-field-' + mpnValidationType) || 'mpnClass'}
              placeholder="Enter MPN"
              value={mpn}
              onChange={onChangeMPN}
              disabled={!!id}
            />
            <FieldValidation fieldName='mpn' show={!validMPNCode} type={mpnValidationType} message={mpnValidationMessage} onClose={closeMPNValueError} />
          </div>
        </Form.Group>
        <Form.Group className="form-textfield">
          <Form.Label className="wnb-label" style={{ paddingLeft: '80px' }}>ATA</Form.Label>
          <div className="field-control marginCls">
            <Form.Control
              className={(!validATA && 'ataClass invalid-field-' + ataValidationType) || 'ataClass'}
              type="ata"
              placeholder="Enter ATA"
              value={ata}
              onChange={onChangeATA}
              onBlur={onBlurATA}
            />
            <FieldValidation show={!validATA} type={ataValidationType} message={ataValidationMessage} onClose={closeATAValueError} />
          </div>
        </Form.Group>
        <Form.Group className="form-textfield">
          <Form.Label className="wnb-label" style={{ paddingLeft: '42px' }}>Vendor<span style={{ color: 'var(--southwest-red)', paddingLeft: '2px' }}>&#10045;</span></Form.Label>
            <div className="field-control marginCls">
                <Form.Control
                    type="vendor"
                    className={(!validVendor && 'vendorClass invalid-field-' + vendorValidationType) || 'vendorClass'}
                    placeholder="Enter Vendor"
                    value={vendor}
                    onChange={onChangeVendor}
                />
                <FieldValidation fieldName='vendor' show={!validVendor} type={vendorValidationType} message={vendorValidationMessage} onClose={closeVendorErrorValue} />
            </div>
        </Form.Group>
        <Form.Group className="form-textfield">
          <Form.Label className="wnb-label" style={{ paddingLeft: '19px' }}>Effectivity<span style={{ color: 'var(--southwest-red)', paddingLeft: '2px' }}>&#10045;</span></Form.Label>
          <Select className="effectivity-combobox" options={activeEffOptions} isMulti placeholder="Select Effectivity" value={effectivities} onChange={onChangeEffectivity} />
        </Form.Group>
        <Form.Group className="form-textfield">
          <Form.Label className="wnb-label">Description<span style={{ color: 'var(--southwest-red)', paddingLeft: '4px' }}>&#10045;</span></Form.Label>
          <div className="field-control">
            <Form.Control
              type="description"
              placeholder="Enter Description"
              value={description}
              className={(!validDescription && 'descClass invalid-field-' + descriptionValidationType) || 'descClass'}
              onChange={onChangeDescription}
            />
            <FieldValidation fieldName='description' show={!validDescription} type={descriptionValidationType} message={descriptionValidationMessage} onClose={closeDescriptionErrorValue} />
          </div>
        </Form.Group>

        <Form.Group className="action-panel">
          <Button disabled={!!!partStatus ||
            !!!wbStatus ||
            !!!validMPNCode ||
              !!!mpn ||
            !!!validVendor ||
              !!!vendor ||
            !!!effectivities ||
            effectivities.length < 1 ||
            !!!validDescription ||
              !!!description ||
            !!!validATA ||
            (ata && ata.length !== 2) ||
            inputDuplicateValue === true}
            onClick={onSave}>
            {saveLabel}
          </Button>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        </Form.Group>
      </Form>
      <WnbModal
        title="Warning"
        message="Are you sure you wish to cancel?  Clicking YES will reset all data."
        handleCancel={onClickModalNo}
        handleYes={onClickModalYes}
        showAlert={showAlert}
      />
      <WnbModalSuccess
        title="Success"
        message={saveMessage}
        handleOk={onClickOkSaveSuccess}
        handleNew={onClickNew}
        handleShowList={onClickShowList}
        showAlert={showSaveAlert}
      />
      <WnbModalError
        title="Error"
        message={saveErrorMessage}
        handleOk={onClickOkSaveError}
        showAlert={showNotSavedAlert}
      />
    </div>
  );
};

export default PartForm;
